import React, { useContext, useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import { AssetContext } from './SingleAsset'
import { NumberPlate } from '../../vehicles/VehicleComponents/VehicleHero'

// Puts the asset directly on a vehicle. The server clears `parentAsset` at
// the same time - on a vehicle means no longer inside another asset.
const AssignToVehicleDialog = ({ open, onClose }) => {
  const { asset, setAsset } = useContext(AssetContext)
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const [vehicleList, setVehicleList] = useState([])
  const [loadingVehicles, setLoadingVehicles] = useState(false)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setSelectedVehicle(null)
    setLoadingVehicles(true)
    API.get('/vehicleList')
      .then((res) => res.status === 200 && setVehicleList(res.data ?? []))
      .catch(() => setVehicleList([]))
      .finally(() => setLoadingVehicles(false))
  }, [open])

  const handleAssign = () => {
    if (!selectedVehicle || saving) return
    setSaving(true)
    const comment = `Asset assigned to ${selectedVehicle.callSign}`
    API.put('/asset/' + asset._id, {
      vehicle: selectedVehicle._id,
      $push: { comments: { comment, author: user._id } },
    })
      .then((res) => {
        if (res.status === 200) {
          setAsset((prev) => ({
            ...prev,
            vehicle: selectedVehicle,
            parentAsset: null,
            comments: [
              ...(prev?.comments ?? []),
              {
                comment,
                author: {
                  _id: user._id,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  photograph: user.photograph,
                },
                createdAt: new Date(),
                _id: new Date().toISOString(),
              },
            ],
          }))
          setToast({ message: `Assigned to ${selectedVehicle.callSign}` })
          onClose()
        } else {
          setToast({
            message: 'Unable to update asset. Please try again later.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to update asset. Please try again later.',
          severity: 'error',
        }),
      )
      .finally(() => setSaving(false))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 800 }}>
        Assign {asset.assetID} to a vehicle
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {asset.parentAsset?.assetID
            ? `This asset is currently recorded inside ${asset.parentAsset.assetID}. Assigning it to a vehicle takes it out of that asset.`
            : 'The asset will show as aboard the selected vehicle and appear in its Assets tab.'}
        </DialogContentText>
        <Autocomplete
          options={vehicleList}
          loading={loadingVehicles}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          value={selectedVehicle}
          onChange={(e, value) => setSelectedVehicle(value)}
          getOptionLabel={(vehicle) => vehicle.callSign ?? ''}
          renderOption={(props, vehicle) => (
            <Box component="li" {...props} key={vehicle._id}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={700}>{vehicle.callSign}</Typography>
                {vehicle.regPlate && (
                  <NumberPlate regPlate={vehicle.regPlate} size="small" />
                )}
              </Stack>
            </Box>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Vehicle" autoFocus />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedVehicle || saving}
          onClick={handleAssign}
        >
          {saving ? 'Assigning…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AssignToVehicleDialog
